import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Table, Card, Button, Space, message, Popconfirm, Row, Col, 
  Input, Select, Drawer, Form, DatePicker, 
  Tag
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import PageLayout from '../../components/layout/Layout';
import jobs from '../../services/jobs';
import jobType from '../../services/jobType';
import states from '../../services/states';
import companies from '../../services/company';
import dayjs from 'dayjs';
import { auditColumns } from '../../utils/audit';

const { TextArea } = Input;

const JobsPage = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState(null);

  // Fetch jobs data
  const { data, isLoading } = useQuery({
    queryKey: ['jobs'],
    queryFn: async () => {
      const response = await jobs.get();
      return response.data;
    },
  });

  // Fetch dropdown data
  const { data: jobTypes } = useQuery({
    queryKey: ['jobTypes'],
    queryFn: async () => {
      const response = await jobType.get();
      return response;
    },
  });

  const { data: statesData } = useQuery({
    queryKey: ['states'],
    queryFn: async () => {
      const response = await states.get();
      return response;
    },
  });

  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await companies.get('?all=true');
      return response.data.data;
    },
  });

  // Add mutation
  const addMutation = useMutation({
    mutationFn: (values) => {
        console.log(values);
        return jobs.post(values);
    },
    onSuccess: () => {
      message.success('Job successfully added');
      queryClient.invalidateQueries(['jobs']);
      setIsDrawerVisible(false);
    },
    onError: (error) => {
      message.error('Failed to save: ' + (error.response?.data?.message || error.message));
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, values }) => jobs.put(id, values),
    onSuccess: () => {
      message.success('Job successfully updated');
      queryClient.invalidateQueries(['jobs']);
      setIsDrawerVisible(false);
    },
    onError: (error) => {
      message.error('Failed to update: ' + (error.response?.data?.message || error.message));
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => jobs.delete(id),
    onSuccess: () => {
      message.success('Job successfully deleted');
      queryClient.invalidateQueries(['jobs']);
    },
    onError: () => {
      message.error('Failed to delete');
    },
  });

  const handleAdd = () => {
    form.resetFields();
    setEditingId(null);
    setIsDrawerVisible(true);
  };

  const handleEdit = (record) => {
    // Transform the record data to match form expectations
    const formData = {
      ...record,
      company_id: record.company?.id,
      job_type_id: record.job_type?.id,
      state_id: record.job_state?.id,
      is_active: record.is_active ? 1 : 0,  // Convert boolean to 1/0
      expires_at: record.expires_at ? dayjs(record.expires_at) : null
    };
    
    form.setFieldsValue(formData);
    setEditingId(record.id);
    setIsDrawerVisible(true);
  };

  const handleDelete = (id) => {
    deleteMutation.mutate(id);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Transform the values before sending to API
      const transformedValues = {
        ...values,
        is_active: Number(values.is_active), // Ensure is_active is a number
        expires_at: values.expires_at ? values.expires_at.format('YYYY-MM-DD') : null
      };

      if (editingId) {
        updateMutation.mutate({ id: editingId, values: transformedValues });
      } else {
        addMutation.mutate(transformedValues);
      }
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const columns = [
    {
        title: 'Title',
        key: 'title_job_type',
        filteredValue: [search],
        onFilter: (value, record) =>
          record.title.toLowerCase().includes(value.toLowerCase()),
        render: (record) => <>
            <span>{record.title}</span>
            <Tag style={{ marginLeft: 10 }} size="small" color="blue">{record.job_type?.name || ''}</Tag>
        </>,
      },
      {
        title: 'Company',
        dataIndex: ['company', 'name'],
        key: 'company',
      },
      {
        title: 'Job Address',
        key: 'location_job_state',
        render: (record) => `${record.location || ''} - ${record.job_state?.name || ''}`,
      },
      {
        title: 'Status',
        dataIndex: 'is_active',
        key: 'is_active',
        render: (isActive) => (
          <span>{Number(isActive) === 1 ? 'Active' : 'Inactive'}</span>
        ),
      },
    ...auditColumns,
    {
      title: 'Actions',
      fixed: 'right',
      width: 120,
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Are you sure you want to delete this job?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="primary" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PageLayout>
      <Card title="Jobs">
        <Row gutter={[16, 16]} className="table-header">
          <Col flex="1">
            <Input
              placeholder="Search by title"
              prefix={<SearchOutlined />}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              Add New
            </Button>
          </Col>
        </Row>

        <Table
          size='small'
          columns={columns}
          dataSource={data || []}
          loading={isLoading}
          rowKey="id"
        />

        <Drawer
          title={editingId ? "Edit Job" : "Add Job"}
          placement="right"
          onClose={() => setIsDrawerVisible(false)}
          open={isDrawerVisible}
          width={600}
          extra={
            <Space>
              <Button onClick={() => setIsDrawerVisible(false)}>Cancel</Button>
              <Button 
                type="primary" 
                onClick={handleSubmit}
                loading={addMutation.isPending || updateMutation.isPending}
              >
                Submit
              </Button>
            </Space>
          }
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="title"
              label="Title"
              rules={[{ required: true, message: 'Please input the job title!' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="company_id"
              label="Company"
              rules={[{ required: true, message: 'Please select a company!' }]}
            >
              <Select
                placeholder="Select company"
                options={companiesData?.map(company => ({
                  value: company.id,
                  label: company.name
                }))}
              />
            </Form.Item>

            <Form.Item
              name="job_type_id"
              label="Job Type"
              rules={[{ required: true, message: 'Please select a job type!' }]}
            >
              <Select
                placeholder="Select job type"
                options={jobTypes?.map(type => ({
                  value: type.id,
                  label: type.name
                }))}
              />
            </Form.Item>

            <Form.Item
              name="state_id"
              label="State"
              rules={[{ required: true, message: 'Please select a state!' }]}
            >
              <Select
                placeholder="Select state"
                options={statesData?.map(state => ({
                  value: state.id,
                  label: state.name
                }))}
              />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
              rules={[{ required: true, message: 'Please input the description!' }]}
            >
              <TextArea rows={4} />
            </Form.Item>

            <Form.Item
              name="requirements"
              label="Requirements"
              rules={[{ required: true, message: 'Please input the requirements!' }]}
            >
              <TextArea rows={4} />
            </Form.Item>

            <Form.Item
              name="salary_range"
              label="Salary Range"
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="location"
              label="Location"
              rules={[{ required: true, message: 'Please input the location!' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="is_active"
              label="Status"
              initialValue={1}  // Set default value to active
            >
              <Select>
                <Select.Option value={1}>Active</Select.Option>
                <Select.Option value={0}>Inactive</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="expires_at"
              label="Expiry Date"
            >
              <DatePicker />
            </Form.Item>
          </Form>
        </Drawer>
      </Card>
    </PageLayout>
  );
};

export default JobsPage; 